// src/login/superadmin/SuperAdmin.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './SuperAdmin.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.jmkinfosoft.com';

const SuperAdmin = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    role: 'admin'
  });

  // Axios instance with auth header
  const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
  });

  axiosInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Load data from API
  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    if (userRole !== 'super_admin') {
      navigate('/login');
      return;
    }
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/api/users');
      
      if (response.data.success) {
        setUsers(response.data.data.items || []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.response?.data?.message || 'Failed to load users');
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Validation
      if (!formData.name || !formData.mobile || !formData.password) {
        setError('Name, Mobile, and Password are required');
        setLoading(false);
        return;
      }

      const mobileRegex = /^[0-9]{10}$/;
      if (!mobileRegex.test(formData.mobile)) {
        setError('Please enter a valid 10-digit mobile number');
        setLoading(false);
        return;
      }

      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters');
        setLoading(false);
        return;
      }

      const payload = {
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email || '',
        role: formData.role,
        password: formData.password
      };

      const response = await axiosInstance.post('/api/users', payload);
      
      if (response.data.success) {
        setSuccess(`${formData.role === 'admin' ? 'Admin' : 'External Admin'} created successfully!`);
        setFormData({
          name: '',
          email: '',
          mobile: '',
          password: '',
          role: 'admin'
        });
        setShowForm(false);
        await loadUsers();
      }
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.response?.data?.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axiosInstance.patch(`/api/users/${userId}/active`, {
        is_active: !currentStatus
      });

      if (response.data.success) {
        setSuccess(`User ${action}d successfully!`);
        await loadUsers();
      }
    } catch (err) {
      console.error('Error toggling user status:', err);
      setError(err.response?.data?.message || 'Failed to update user status');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userMobile');
    navigate('/login');
  };

  const getStatusBadge = (isActive) => {
    return isActive ? 
      <span className="status-badge status-active">Active</span> : 
      <span className="status-badge status-inactive">Inactive</span>;
  };

  const getRoleBadge = (role) => {
    const roleMap = {
      'super_admin': { class: 'role-super', label: 'Super Admin' },
      'admin': { class: 'role-admin', label: 'Admin' },
      'external_admin': { class: 'role-external', label: 'Ext. Admin' }
    };
    const r = roleMap[role] || { class: 'role-default', label: role };
    return <span className={`role-badge ${r.class}`}>{r.label}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const isSuperAdmin = localStorage.getItem('userRole') === 'super_admin';

  if (!isSuperAdmin) {
    return null;
  }

  // Filter users by role for display
  const adminUsers = users.filter(user => user.role === 'admin' || user.role === 'super_admin');
  const externalUsers = users.filter(user => user.role === 'external_admin');

  return (
    <div className="super-admin-container">
      <div className="super-admin-card">
        {/* Header */}
        <div className="super-admin-header">
          <button className="back-btn" onClick={() => navigate('/')}>
            <i className="fas fa-arrow-left"></i>
          </button>
          <h1>
            <i className="fas fa-user-shield"></i> Super Admin Dashboard
          </h1>
          {/* <button className="logout-btn" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt"></i> Logout
          </button> */}
        </div>

        {/* Stats */}
        {/* <div className="super-admin-stats">
          <div className="stat-card">
            <i className="fas fa-user-cog"></i>
            <div>
              <span className="stat-number">{adminUsers.length}</span>
              <span className="stat-label">Total Admins</span>
            </div>
          </div>
          <div className="stat-card">
            <i className="fas fa-user-tie"></i>
            <div>
              <span className="stat-number">{externalUsers.length}</span>
              <span className="stat-label">External Admins</span>
            </div>
          </div>
          <div className="stat-card">
            <i className="fas fa-user-check"></i>
            <div>
              <span className="stat-number">
                {users.filter(u => u.is_active).length}
              </span>
              <span className="stat-label">Active Users</span>
            </div>
          </div>
          <div className="stat-card">
            <i className="fas fa-users"></i>
            <div>
              <span className="stat-number">{users.length}</span>
              <span className="stat-label">Total Users</span>
            </div>
          </div>
        </div> */}

        {/* Messages */}
        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="success-message">
            <i className="fas fa-check-circle"></i>
            <span>{success}</span>
          </div>
        )}

        {loading && (
          <div className="loading-overlay">
            <i className="fas fa-spinner fa-spin"></i> Loading...
          </div>
        )}

        {/* Action Buttons */}
        <div className="super-admin-actions">
          <button 
            className="action-btn primary" 
            onClick={() => {
              setFormData({
                name: '',
                email: '',
                mobile: '',
                password: '',
                role: 'admin'
              });
              setEditingUser(null);
              setShowForm(true);
            }}
            disabled={loading}
          >
            <i className="fas fa-user-plus"></i> Create Admin / External Admin
          </button>
          {/* <button 
            className="action-btn secondary" 
            onClick={() => {
              setFormData({
                name: '',
                email: '',
                mobile: '',
                password: '',
                role: 'external_admin'
              });
              setEditingUser(null);
              setShowForm(true);
            }}
            disabled={loading}
          >
            <i className="fas fa-user-tie"></i> Create External Admin
          </button> */}
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="form-modal">
            <div className="form-modal-content">
              <div className="form-modal-header">
                <h3>
                  {formData.role === 'admin' ? 'Create New Admin' : 'Create New External Admin'}
                </h3>
                <button 
                  className="form-modal-close" 
                  onClick={() => setShowForm(false)}
                  disabled={loading}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-modal-body">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter full name"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter email (optional)"
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group">
                    <label>Mobile Number *</label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      placeholder="9876543210"
                      maxLength="10"
                      required
                      disabled={loading}
                    />
                    <small>10-digit mobile number</small>
                  </div>
                  <div className="form-group">
                    <label>Password *</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Minimum 8 characters"
                      required
                      disabled={loading}
                    />
                    <small>Minimum 8 characters</small>
                  </div>
                  <div className="form-group">
                    <label>Role</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      disabled={loading}
                    >
                      <option value="admin">Admin</option>
                      <option value="external_admin">External Admin</option>
                    </select>
                  </div>
                </div>
                <div className="form-modal-footer">
                  <button 
                    type="button" 
                    className="cancel-btn" 
                    onClick={() => setShowForm(false)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="save-btn" disabled={loading}>
                    <i className="fas fa-plus"></i>
                    {loading ? ' Creating...' : ' Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Users List */}
        <div className="admin-table-section">
          <h2><i className="fas fa-users"></i> All Staff Users</h2>
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && users.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="loading-data">
                      <i className="fas fa-spinner fa-spin"></i> Loading...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="no-data">No users found</td>
                  </tr>
                ) : (
                  users.map(user => (
                    <tr key={user.id}>
                      <td><strong>{user.name}</strong></td>
                      <td>{user.email || 'N/A'}</td>
                      <td>{user.mobile}</td>
                      <td>{getRoleBadge(user.role)}</td>
                      <td>{getStatusBadge(user.is_active)}</td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>
                        {user.role !== 'super_admin' && (
                          <button 
                            className={`icon-btn ${user.is_active ? 'deactivate' : 'activate'}`}
                            onClick={() => handleToggleStatus(user.id, user.is_active)}
                            title={user.is_active ? 'Deactivate' : 'Activate'}
                            disabled={loading}
                          >
                            <i className={`fas ${user.is_active ? 'fa-ban' : 'fa-check-circle'}`}></i>
                          </button>
                        )}
                        {user.role === 'super_admin' && (
                          <span className="protected-badge">
                            <i className="fas fa-lock"></i> Protected
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdmin;